import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-shop-erstellen-lassen";
const TITLE = "Shopify Shop erstellen lassen: Kosten, Ablauf, Auswahl";
const DESCRIPTION =
  "Shopify Shop erstellen lassen: realistische Kosten, Ablauf in Wochen, Auswahl zwischen Freelancer und Team, und woran Sie ein belastbares Angebot erkennen.";

const faqs = [
  {
    q: "Was kostet es, einen Shopify Shop erstellen zu lassen?",
    a: "Ein sauber aufgesetzter Shop auf Basis eines guten Themes liegt im DACH-Raum meist zwischen 3.000 und 12.000 €. Individuelle Entwicklung, viele Produkte, mehrere Sprachen oder Schnittstellen zu ERP und Versand treiben den Preis auf 15.000 € und mehr.",
  },
  {
    q: "Wie lange dauert der Aufbau eines Shopify Shops?",
    a: "Mit vorhandenen Texten und Bildern sind vier bis sechs Wochen realistisch. Der häufigste Verzögerungsgrund ist nicht die Technik, sondern fehlende Produktdaten und Inhalte auf Kundenseite.",
  },
  {
    q: "Brauche ich ein individuelles Theme?",
    a: "In den meisten Fällen nicht. Ein solides Standard-Theme mit sauberer Anpassung ist schneller, günstiger und langfristig wartbarer. Individuelle Entwicklung lohnt sich erst bei besonderen Anforderungen wie Konfiguratoren oder B2B-Preislogiken.",
  },
  {
    q: "Was muss ich als Auftraggeber liefern?",
    a: "Produktdaten inklusive Varianten und Preisen, Bilder, Texte, Versand- und Zahlungsbedingungen sowie Rechtstexte. Je vollständiger dieses Paket ist, desto günstiger und schneller wird das Projekt.",
  },
  {
    q: "Was passiert nach dem Launch?",
    a: "Der Launch ist der Anfang, nicht das Ziel. Danach entscheidet die Kaufabschlussrate über den Umsatz, deshalb sollte ein Budget für Messung und Conversion-Arbeit in den ersten Monaten eingeplant sein.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-shop-erstellen-lassen")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Aufbau"
      title="Shopify Shop erstellen lassen: was es kostet und wie es abläuft"
      intro="Wer einen Shop bauen lässt, kauft selten nur Technik. Er kauft eine Entscheidung darüber, wie schnell verkauft wird und wie teuer Änderungen später sind. Dieser Ratgeber sortiert Kosten, Ablauf und Auswahlkriterien."
      source="ratgeber-shop-erstellen"
      sections={[
        {
          h: "Realistische Kosten nach Umfang",
          p: [
            "Die Spannen unten stammen aus Angeboten, die mir Shopbetreiber regelmäßig zeigen. Sie ersetzen kein Angebot, verhindern aber Fehleinschätzungen in beide Richtungen.",
          ],
          list: [
            "Starter-Shop mit Standard-Theme, wenige Produkte: 3.000 bis 6.000 €",
            "Markenshop mit angepasstem Theme und Content: 6.000 bis 12.000 €",
            "Individuelle Entwicklung, B2B oder mehrere Märkte: ab 15.000 €",
            "Migration von WooCommerce oder Shopware: 4.000 bis 15.000 € je nach Datenlage",
            "Laufende Kosten Shopify-Plan und Apps: 40 bis 300 € pro Monat",
          ],
        },
        {
          h: "Ablauf in vier bis sechs Wochen",
          p: [
            "Woche eins klärt Sortiment, Versandlogik, Zahlungsarten und Struktur. Woche zwei und drei bauen Theme, Produktvorlagen und Checkout-Einstellungen. Woche vier ist für Inhalte, Tests und Rechtstexte reserviert.",
            "Wenn ein Projekt aus dem Ruder läuft, liegt es fast immer an Inhalten, die erst während des Aufbaus entstehen. Wer Texte und Bilder vorher fertig hat, spart oft mehrere Tausend Euro.",
          ],
        },
        {
          h: "Freelancer oder Team, was passt wann?",
          p: [
            "Ein einzelner Spezialist ist schneller und direkter, solange die Aufgabe klar umrissen ist: Shop aufsetzen, migrieren, Conversion verbessern. Ein größeres Team lohnt sich, wenn parallel Fotografie, Werbung, Content und Entwicklung laufen sollen.",
            "Entscheidend ist nicht die Größe, sondern wer die Arbeit tatsächlich macht. Fragen Sie im Erstgespräch, wer im Projekt am Code sitzt.",
          ],
        },
        {
          h: "Woran Sie ein belastbares Angebot erkennen",
          p: [
            "Ein gutes Angebot beschreibt Ergebnis, Umfang und Grenzen, nicht nur eine Liste von Leistungen. Es nennt, was nicht enthalten ist, und es nennt eine Zahl, an der Erfolg gemessen wird.",
          ],
          list: [
            "Festpreis mit klar definiertem Umfang statt offener Stundenschätzung",
            "Benannte Kennzahlen für die Zeit nach dem Launch",
            "Übergabe im Shopify-Editor pflegbar, ohne Abhängigkeit vom Dienstleister",
            "Klare Aussage zu Apps: jede App kostet Geschwindigkeit",
          ],
        },
        {
          h: "Nach dem Launch beginnt die eigentliche Arbeit",
          p: [
            "Ein neuer Shop startet selten über 1,5 Prozent Kaufabschlussrate. Der Unterschied zwischen 1,4 und 2,2 Prozent ist bei gleichem Traffic ein Umsatzunterschied von rund 55 Prozent, ohne einen Euro mehr Werbebudget.",
            "Deshalb gehört ein Budget für Messung, Analyse und gezielte Verbesserung in die Planung, nicht erst in das Jahr danach.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/preise", label: "Preise ansehen" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten im Überblick" },
        { to: "/roi-rechner", label: "Potenzial berechnen" },
      ]}
    />
  );
}
